/**
 * Scheduled tasks that run inside one Work chat (InferenceTask.workSession).
 *
 * Backs the Tasks section of the Sources panel: the same tasks the /tasks page
 * manages, filtered to this thread, plus the create/edit/toggle/delete
 * actions wired through the shared CreateTaskModal so the two surfaces never
 * diverge.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useFocusEffect } from 'expo-router';
import useAppActive from '@/hooks/useAppActive';
import { useInferenceTasksStore, useModalStore, useToastStore } from '@/store';
import { useModalHelper } from '@/hooks/useModalHelper';
import { useTranslation } from '@/hooks/useTranslation';

const EMPTY = [];

export default function useWorkChatTasks({ workSessionId, conversationId, chatTitle, enabled = true, taskTransport }) {
  const { t } = useTranslation();
  const { openModal } = useModalStore((state) => state.actions);
  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();
  const { addToast } = useToastStore((state) => state.actions);
  const tasksById = useInferenceTasksStore((state) => state.tasks);
  const fetchWorkSessionTasks = useInferenceTasksStore((state) => state.fetchWorkSessionTasks);
  const toggleTask = useInferenceTasksStore((state) => state.toggleTask);
  const deleteTask = useInferenceTasksStore((state) => state.deleteTask);
  const cloudUpdating = useInferenceTasksStore((state) => state.isLoading.update);
  const [nativeUpdating, setNativeUpdating] = useState(false);
  const nativeMutation = useRef(false);
  const isUpdating = taskTransport ? nativeUpdating : cloudUpdating;
  const [nativeTasks, setNativeTasks] = useState(null);

  const isAppActive = useAppActive();
  const active = enabled && (!!taskTransport || !!workSessionId);
  const scopeKey = taskTransport?.scopeKey || workSessionId;

  const tasks = useMemo(() => {
    if (!active) return EMPTY;
    if (taskTransport) return nativeTasks?.scopeKey === scopeKey ? nativeTasks.tasks : EMPTY;
    const wanted = String(workSessionId);
    return Object.values(tasksById)
      .filter((task) => task && String(task.workSessionId || '') === wanted)
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [active, tasksById, workSessionId, nativeTasks, scopeKey, taskTransport]);

  const [loadState, setLoadState] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey(key => key + 1), []);

  useFocusEffect(useCallback(() => {
    if (!active || !isAppActive) return;
    let cancelled = false;
    setLoadState({ scopeKey, refreshKey, loading: true, error: null });
    const request = taskTransport ? taskTransport.list() : fetchWorkSessionTasks(workSessionId);
    request.then(result => {
      if (!cancelled) {
        if (taskTransport) setNativeTasks({ ...result, scopeKey });
        setLoadState({ scopeKey, refreshKey, loading: false, error: null });
      }
    }).catch((error) => {
      if (cancelled) return;
      console.warn('[useWorkChatTasks] fetch failed', error?.message || error);
      setLoadState({ scopeKey, refreshKey, loading: false, error });
      addToast(t('pages.tasks.loadFailed'), 'error');
    });
    return () => { cancelled = true; };
  }, [active, addToast, fetchWorkSessionTasks, isAppActive, refreshKey, t, workSessionId, scopeKey, taskTransport]));

  const confirmDelete = useCallback((taskId) => {
    showConfirmationModal({
      title: t('pages.tasks.deleteTitle'),
      message: t('pages.tasks.deleteMessage'),
      confirmText: t('common.delete'),
      isDangerous: true,
      onConfirm: async () => {
        try {
          if (taskTransport) await taskTransport.remove(tasks.find(task => task.id === taskId));
          else await deleteTask(taskId);
          refresh();
          addToast(t('pages.tasks.deleteSuccess'), 'success');
        } catch (error) {
          addToast(t('pages.tasks.deleteFailed'), 'error');
        }
      },
    });
  }, [addToast, deleteTask, showConfirmationModal, t, taskTransport, tasks, refresh]);

  const onModalResult = useCallback((result) => {
    if (result?.action === 'delete' && result?.taskId) confirmDelete(result.taskId);
    else refresh();
  }, [confirmDelete, refresh]);

  const addTask = useCallback(() => {
    if (!active) return;
    openModal('CreateTaskModal', {
      workSessionId,
      workChatTitle: chatTitle,
      conversationId,
      taskTransport,
      onSuccess: onModalResult,
    });
  }, [active, chatTitle, conversationId, onModalResult, openModal, workSessionId, taskTransport]);

  const editTask = useCallback((task) => {
    if (!active || !task?.id) return;
    openModal('CreateTaskModal', {
      task,
      workSessionId,
      workChatTitle: chatTitle,
      taskTransport,
      onSuccess: onModalResult,
    });
  }, [active, chatTitle, onModalResult, openModal, workSessionId, taskTransport]);

  const setTaskEnabled = useCallback(async (task, isEnabled) => {
    if (!active || !task?.id || isUpdating || nativeMutation.current) return;
    try {
      if (taskTransport) { nativeMutation.current = true; setNativeUpdating(true); await taskTransport.update(task, { isEnabled }); refresh(); }
      else await toggleTask(task.id, isEnabled);
    } catch (error) {
      addToast(error?.message || t('tasks.failedToSave'), 'error');
    } finally {
      if (taskTransport) { nativeMutation.current = false; setNativeUpdating(false); }
    }
  }, [active, addToast, isUpdating, t, toggleTask, taskTransport, refresh]);

  const removeTask = useCallback((task) => {
    if (active && task?.id) confirmDelete(task.id);
  }, [active, confirmDelete]);

  const isLoading = active && (loadState?.scopeKey !== scopeKey || loadState.refreshKey !== refreshKey || loadState.loading);
  const loadError = active && loadState?.scopeKey === scopeKey ? loadState.error : null;

  return { tasks, canAddTask: active && (!taskTransport || nativeTasks?.scopeKey === scopeKey && nativeTasks.available), isUpdating, isLoading, loadError, refresh, addTask, editTask, setTaskEnabled, removeTask };
}
